import { Network } from '@puzzlehq/sdk';
import { trpcReact } from '@src/data/trpc';
import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useArcadeStoreState } from './useArcadeStoreState';
import { useAuth, useSettings } from '@src/data/settings';
import { ProductSKU, zodArcadeStoreUserProduct } from 'jigsaw';
import { getEmojiFlag, TCountryCode } from 'countries-list';
import toast from 'react-hot-toast';
import { z } from 'zod';

const network: Network = import.meta.env.VITE_NETWORK!;

type UseArcadeStoreServiceProps = {
  serviceSlug: string;
};
export const useArcadeStoreService = ({
  serviceSlug,
}: UseArcadeStoreServiceProps) => {
  return trpcReact.getStoreService.useQuery(
    { network, serviceSlug },
    { retry: 5 }
  );
};

export const useArcadeStoreServices = () => {
  const [countryCode] = useSettings((state) => [state.countryCode]);
  return trpcReact.getStoreServices.useQuery(
    { network, userCountry: countryCode },
    { retry: 5 }
  );
};

type UseArcadeStoreSKUsForServiceProps = {
  serviceSlug: string;
};
export const useArcadeStoreSKUsForService = ({
  serviceSlug,
}: UseArcadeStoreSKUsForServiceProps) => {
  const [countryCode] = useSettings((state) => [state.countryCode]);

  return trpcReact.getProductsSKUsForStoreService.useQuery(
    {
      network,
      serviceSlug: serviceSlug,
      userCountry: countryCode,
    },
    {
      retry: 5,
    }
  );
};

export const useArcadeStoreSKUsForCountry = () => {
  const [countryCode] = useSettings((state) => [state.countryCode]);

  return trpcReact.getProductsSKUsForCountry.useQuery(
    {
      network,
      userCountry: countryCode,
    },
    {
      retry: 5,
    }
  );
};

type UseArcadeStoreSKUProps = {
  skuId: number;
};
export const useArcadeStoreSKU = ({ skuId }: UseArcadeStoreSKUProps) => {
  const [countryCode] = useSettings((state) => [state.countryCode]);

  return trpcReact.getProductSKU.useQuery(
    {
      network,
      productSkuId: skuId,
      userCountry: countryCode,
    },
    {
      retry: 5,
    }
  );
};

export const useArcadeStoreProgramIds = () => {
  const query = trpcReact.getStoreChainData.useQuery(
    {
      network,
    },
    {
      staleTime: 600_000,
      retry: 5,
    }
  );

  const ids = useMemo(() => {
    if (query.data === undefined) return;

    const inventoryProgramIds = _.uniq(
      query.data.map((data) => data.inventoryProgramId)
    );
    const storeProgramIds = _.uniq(
      query.data.flatMap((data) =>
        Object.values(data.programFunctionIds).map((data_1) => data_1.programId)
      )
    );

    return {
      inventoryProgramIds,
      storeProgramIds,
    };
  }, [JSON.stringify(query.data)]);

  const inventoryProgramIds = ids?.inventoryProgramIds;
  const storeProgramIds = ids?.storeProgramIds;

  return {
    inventoryProgramIds,
    storeProgramIds,
  };
};

export const usePendingOrders = () => {
  const { account } = useArcadeStoreState();
  return trpcReact.getOrders.useQuery(
    {
      address: account?.address!,
      network: network,
      status: 'pending',
    },
    {
      enabled: !!account,
      staleTime: 5_000,
    }
  );
};

export const useHasOrderedToday = () => {
  const { account } = useArcadeStoreState();
  const orders = trpcReact.getOrders.useQuery(
    {
      address: account?.address!,
      network: network,
      status: 'all',
    },
    {
      enabled: !!account,
      staleTime: 5_000,
    }
  );

  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);

  const todaysOrders = orders.data?.filter(
    (order) =>
      !order.purchaseError &&
      !order.fulfillmentError &&
      order.purchased.getTime() > yesterday.getTime()
  );

  const hasOrderedToday = (todaysOrders?.length ?? 0) > 0;

  useEffect(() => {
    console.log('hasOrderedToday', hasOrderedToday);
  }, [hasOrderedToday]);

  return hasOrderedToday;
};

export const usePendingPurchasesForSKU = (skuId: number) => {
  const query = usePendingOrders();
  const data = query.data;
  const newData = data?.filter(
    (order) =>
      order.skuId === skuId && !order.purchaseError && !order.fulfillmentError
  );

  return {
    ...query,
    data: newData,
    hasPendingPurchases: newData && newData.length > 0,
  };
};

export const useUserCountryCode = () => {
  return trpcReact.getUserCountryCode.useQuery(undefined, {
    staleTime: Infinity,
  });
};

type DisplayFlagProps = {
  sku?: ProductSKU;
};
export const useDisplayFlag = ({ sku }: DisplayFlagProps) => {
  const { countryCode: userCountryCode } = useArcadeStoreState();

  const displayFlag: string | undefined = useMemo(() => {
    if (!sku) return undefined;

    const countries = sku.countries ?? sku.service.countries;
    if (userCountryCode && countries?.includes(userCountryCode)) {
      return getEmojiFlag(userCountryCode as TCountryCode);
    }
    const countryCode = sku.countries?.[0];
    return countryCode ? getEmojiFlag(countryCode as TCountryCode) : undefined;
  }, [
    userCountryCode,
    JSON.stringify(sku?.countries ?? sku?.service.countries),
  ]);

  return displayFlag;
};

export const useRewards = () => {
  const { account } = useArcadeStoreState();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getUserProducts.useQuery(
    {
      address: account?.address!,
      network: network,
      auth: auth,
      challengeAuth: challengeAuth,
    },
    {
      enabled: !!account && (!!auth || !!challengeAuth),
    }
  );
};

export type Reward = z.infer<typeof zodArcadeStoreUserProduct>;

export const useReward = (id: string) => {
  const { account } = useArcadeStoreState();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);
  const queryParams = {
    address: account?.address!,
    network: network,
    id,
    auth: auth,
    challengeAuth: challengeAuth,
  };
  const trpcReactUtils = trpcReact.useUtils();

  const query = trpcReact.getUserProduct.useQuery(queryParams, {
    enabled: !!account && (!!auth || !!challengeAuth),
  });

  const { isLoading, mutate } = trpcReact.setUserProductUsed.useMutation({
    onMutate: async (variables) => {
      // Optimistically update the cache
      trpcReactUtils.getUserProduct.setData(queryParams, (old) => {
        if (!old) return old;
        return { ...old, used: variables.used };
      });
    },
    onError: (_, variables) => {
      // Revert the optimistic update on error
      trpcReactUtils.getUserProduct.setData(queryParams, (old) => {
        if (!old) return old;
        return { ...old, used: !variables.used };
      });
      toast.error(`Could not mark as ${variables.used ? 'used' : 'unused'}.`);
    },
    onSuccess: (_, variables) => {
      toast.success(`Marked as ${variables.used ? 'used' : 'unused'}!`);
    },
  });

  const markUsedStatus = useCallback(
    (used: boolean) => {
      if (!account) {
        toast.error('Connect wallet first!');
        return;
      } else if (!auth) {
        toast.error('UNAUTHORIZED');
        return;
      }
      mutate({
        network,
        address: account.address,
        id,
        used,
        auth,
      });
    },
    [JSON.stringify(account), network, id]
  );

  return {
    ...query,
    isMarkingUsed: isLoading,
    markUsedStatus,
  };
};
